import { useCallback, useEffect, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminService } from "../../features/admin/service";
import { dealsService } from "../../features/deals/service";
import type {
  Broadcast,
  BroadcastDeliveryMode,
  BroadcastPreview,
  BroadcastRole,
  PaginatedResponse,
  NotificationCatalogEntry,
  NotificationCatalogEntryWithStatus,
  UserNotificationStatus,
} from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import { useAlertModal } from "../../components/admin/AlertModal";
import Pagination from "../../components/admin/Pagination";
import Spinner from "../../components/ui/Spinner";
import { useAdminToast } from "../../context/AdminToastContext";

type ChipType = "country" | "city" | "tag" | "activity";

const CHIP_COLORS: Record<ChipType, string> = {
  country: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  city: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  tag: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  activity: "bg-pink-500/20 text-pink-300 border-pink-500/30",
};

interface FilterOptions {
  countries: string[];
  cities: string[];
  tags: string[];
  activities: string[];
}

const ROLE_OPTIONS: { value: BroadcastRole; label: string; icon: string }[] = [
  { value: "all", label: "Everyone", icon: "🌐" },
  { value: "buyer", label: "Buyers", icon: "🛒" },
  { value: "seller", label: "Sellers", icon: "🏪" },
];

const STATUS_STYLES: Record<string, string> = {
  sent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  scheduled: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
};

const ChipSection: React.FC<{
  type: ChipType;
  label: string;
  icon: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}> = ({ type, label, icon, options, selected, onToggle }) => {
  const [search, setSearch] = useState("");
  const q = search.toLowerCase().trim();
  const visible = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-300 flex items-center gap-1.5">
          <span>{icon}</span>
          {label}
          {selected.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
              {selected.length}
            </span>
          )}
        </label>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search…`}
          className="px-2 py-1 text-xs rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 w-32"
        />
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1 rounded-lg bg-gray-800/40 border border-gray-800">
        {visible.length === 0 && <span className="text-xs text-gray-500 p-1">No options</span>}
        {visible.map((value) => {
          const active = selected.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => onToggle(value)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                active ? CHIP_COLORS[type] + " shadow-sm" : "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600"
              }`}
            >
              {value}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ChannelBadges: React.FC<{ channels: { socket: boolean; bell: boolean; fcm: boolean } }> = ({ channels }) => (
  <div className="flex gap-1.5 flex-wrap">
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${channels.socket ? "bg-sky-500/15 text-sky-300 border-sky-500/30" : "bg-gray-800 text-gray-600 border-gray-700"}`}>
      Socket
    </span>
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${channels.bell ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-gray-800 text-gray-600 border-gray-700"}`}>
      Bell
    </span>
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${channels.fcm ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-gray-800 text-gray-600 border-gray-700"}`}>
      FCM
    </span>
  </div>
);

// Human-readable audience labels for the `applies_to` field — mirrors the
// backend's relevance_for_user() matcher in app/modules/admin/routes.py so
// the admin can see, at a glance, who a given event is even eligible to reach
// before drilling into a specific user's "Reaches this user?" column.
const AUDIENCE_META: Record<string, { label: string; icon: string; color: string; note: string }> = {
  "": { label: "Everyone", icon: "🌐", color: "bg-gray-800 text-gray-300 border-gray-700", note: "No audience restriction — fires for whichever user_id the event targets." },
  self: { label: "Self", icon: "👤", color: "bg-sky-500/15 text-sky-300 border-sky-500/30", note: "Fires for a user on their own actions (e.g. profile update, login alert)." },
  seller: { label: "Sellers only", icon: "🏪", color: "bg-violet-500/15 text-violet-300 border-violet-500/30", note: "Only reaches users with role=seller (inventory/deal lifecycle events)." },
  buyer: { label: "Buyers only", icon: "🛒", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", note: "Only reaches users with role!=seller." },
  matching_buyers: { label: "Matching buyers", icon: "🎯", color: "bg-pink-500/15 text-pink-300 border-pink-500/30", note: "Buyers whose preferred tags/destinations/interest graph match the deal — not a broad blast." },
  topic_subscribers: { label: "Topic subscribers", icon: "📡", color: "bg-amber-500/15 text-amber-300 border-amber-500/30", note: "Anyone subscribed to the relevant FCM topic (feed_deal_*, feed_news_*, feed_webinar) — needs an active device token." },
  owner: { label: "Content owner", icon: "❤️", color: "bg-rose-500/15 text-rose-300 border-rose-500/30", note: "The seller/author whose deal, webinar, or post someone else just liked." },
};

const AudienceBadge: React.FC<{ appliesTo?: string | null }> = ({ appliesTo }) => {
  const meta = AUDIENCE_META[appliesTo || ""] || AUDIENCE_META[""];
  return (
    <div>
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border inline-flex items-center gap-1 ${meta.color}`}>
        <span>{meta.icon}</span>{meta.label}
      </span>
      <div className="text-[10px] text-gray-500 mt-1 max-w-[180px] leading-snug">{meta.note}</div>
    </div>
  );
};

const NotificationCatalogTable: React.FC<{
  items: (NotificationCatalogEntry | NotificationCatalogEntryWithStatus)[];
  showRelevance?: boolean;
  editable?: boolean;
  onChanged?: (items: NotificationCatalogEntry[]) => void;
}> = ({ items, showRelevance, editable, onChanged }) => {
  const [editingType, setEditingType] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftClickAction, setDraftClickAction] = useState("");
  const [draftAppliesTo, setDraftAppliesTo] = useState("");
  const [draftSourceType, setDraftSourceType] = useState("");
  const [draftSelectedTab, setDraftSelectedTab] = useState("");
  const [draftSocket, setDraftSocket] = useState(true);
  const [draftBell, setDraftBell] = useState(true);
  const [draftFcm, setDraftFcm] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const startEdit = (entry: NotificationCatalogEntry) => {
    setEditingType(entry.notif_type);
    setDraftTitle(entry.title);
    setDraftBody(entry.body);
    setDraftClickAction(entry.click_action || "");
    setDraftAppliesTo(entry.applies_to || "");
    setDraftSourceType(entry.source_type || "");
    setDraftSelectedTab(entry.selected_tab || "");
    setDraftSocket(entry.channels.socket);
    setDraftBell(entry.channels.bell);
    setDraftFcm(entry.channels.fcm);
    setRowError(null);
  };

  const cancelEdit = () => { setEditingType(null); setRowError(null); };

  const saveEdit = async (notifType: string) => {
    setSaving(true);
    setRowError(null);
    try {
      const data = await adminService.updateNotificationTemplate(notifType, {
        title: draftTitle,
        body: draftBody,
        click_action: draftClickAction.trim() || null,
        applies_to: draftAppliesTo || null,
        source_type: draftSourceType.trim() || null,
        selected_tab: draftSelectedTab.trim() || null,
        channels: { socket: draftSocket, bell: draftBell, fcm: draftFcm },
      });
      onChanged?.(data.items);
      setEditingType(null);
    } catch (err: any) {
      setRowError(err?.response?.data?.detail || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (notifType: string) => {
    if (!confirm(`Are you sure you want to delete ${notifType}?`)) return;
    setSaving(true);
    setRowError(null);
    try {
      const data = await adminService.deleteNotificationTemplate(notifType);
      onChanged?.(data.items);
      setEditingType(null);
    } catch (err: any) {
      setRowError(err?.response?.data?.detail || "Failed to delete template.");
    } finally {
      setSaving(false);
    }
  };

  const [isCreating, setIsCreating] = useState(false);
  const [newType, setNewType] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newEvent, setNewEvent] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newClickAction, setNewClickAction] = useState("");
  const [newAppliesTo, setNewAppliesTo] = useState("");
  const [newSourceType, setNewSourceType] = useState("");
  const [newSelectedTab, setNewSelectedTab] = useState("");
  const [newSocket, setNewSocket] = useState(true);
  const [newBell, setNewBell] = useState(true);
  const [newFcm, setNewFcm] = useState(true);

  const resetCreateForm = () => {
    setIsCreating(false);
    setNewType("");
    setNewCategory("");
    setNewEvent("");
    setNewTitle("");
    setNewBody("");
    setNewClickAction("");
    setNewAppliesTo("");
    setNewSourceType("");
    setNewSelectedTab("");
    setNewSocket(true);
    setNewBell(true);
    setNewFcm(true);
    setRowError(null);
  };

  const saveNew = async () => {
    setSaving(true);
    setRowError(null);
    try {
      const data = await adminService.createNotificationTemplate({
        notif_type: newType.trim(),
        category: newCategory.trim(),
        event: newEvent.trim(),
        title: newTitle.trim(),
        body: newBody.trim(),
        click_action: newClickAction.trim() || null,
        applies_to: newAppliesTo || null,
        source_type: newSourceType.trim() || null,
        selected_tab: newSelectedTab.trim() || null,
        channels: { socket: newSocket, bell: newBell, fcm: newFcm },
      });
      onChanged?.(data.items);
      resetCreateForm();
    } catch (err: any) {
      setRowError(err?.response?.data?.detail || "Failed to create template.");
    } finally {
      setSaving(false);
    }
  };

  const uniqueCategories = Array.from(new Set(items.map((i) => i.category).filter(Boolean)));
  const uniqueEvents = Array.from(new Set(items.map((i) => i.event).filter(Boolean)));
  const uniqueClickActions = Array.from(new Set(items.map((i) => (i as NotificationCatalogEntry).click_action).filter(Boolean)));
  const uniqueSourceTypes = Array.from(new Set(items.map((i) => (i as NotificationCatalogEntry).source_type).filter(Boolean)));
  const uniqueSelectedTabs = Array.from(new Set(items.map((i) => (i as NotificationCatalogEntry).selected_tab).filter(Boolean)));

  return (
    <div className="overflow-x-auto">
      <datalist id="category-options">
        {uniqueCategories.map(c => <option key={c} value={c} />)}
      </datalist>
      <datalist id="event-options">
        {uniqueEvents.map(c => <option key={c} value={c} />)}
      </datalist>
      <datalist id="click-action-options">
        {uniqueClickActions.map(c => <option key={c as string} value={c as string} />)}
      </datalist>
      <datalist id="source-type-options">
        {uniqueSourceTypes.map(c => <option key={c as string} value={c as string} />)}
      </datalist>
      <datalist id="selected-tab-options">
        {uniqueSelectedTabs.map(c => <option key={c as string} value={c as string} />)}
      </datalist>

      {editable && (
        <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900">
          <div className="text-sm text-gray-400">Total: {items.length} templates</div>
          {!isCreating && (
            <button onClick={() => setIsCreating(true)} className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold">
              + Add New Template
            </button>
          )}
        </div>
      )}
      {isCreating && (
        <div className="p-4 bg-gray-800/40 border-b border-gray-800 space-y-3">
          <h3 className="text-sm font-semibold text-white">Create New Notification Template</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="Type (e.g. system.alert)" className="px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white" />
            <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} list="category-options" placeholder="Category (e.g. system)" className="px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white" />
            <input value={newEvent} onChange={(e) => setNewEvent(e.target.value)} list="event-options" placeholder="Event (e.g. alert)" className="px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white" />
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Title" className="px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white sm:col-span-2" />
            <input value={newClickAction} onChange={(e) => setNewClickAction(e.target.value)} list="click-action-options" placeholder="Click Action (Flutter route)" className="px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white" />
            <select value={newAppliesTo} onChange={(e) => setNewAppliesTo(e.target.value)} className="px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white">
              {Object.entries(AUDIENCE_META).map(([value, meta]) => (
                <option key={value || "everyone"} value={value}>{meta.icon} {meta.label}</option>
              ))}
            </select>
            <input value={newSourceType} onChange={(e) => setNewSourceType(e.target.value)} list="source-type-options" placeholder="source_type (FCM data field, e.g. deal_edit)" className="px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white" />
            <input value={newSelectedTab} onChange={(e) => setNewSelectedTab(e.target.value)} list="selected-tab-options" placeholder="selected_tab (FCM data field, e.g. inventory)" className="px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white" />
          </div>
          <textarea value={newBody} onChange={(e) => setNewBody(e.target.value)} rows={2} placeholder="Body message..." className="w-full px-2 py-1.5 text-sm rounded bg-gray-900 border border-gray-700 text-white resize-y" />
          <div className="flex items-center gap-4 text-xs text-gray-300">
             <label className="flex items-center gap-1.5"><input type="checkbox" checked={newSocket} onChange={(e) => setNewSocket(e.target.checked)} /> Socket</label>
             <label className="flex items-center gap-1.5"><input type="checkbox" checked={newBell} onChange={(e) => setNewBell(e.target.checked)} /> Bell</label>
             <label className="flex items-center gap-1.5"><input type="checkbox" checked={newFcm} onChange={(e) => setNewFcm(e.target.checked)} /> FCM</label>
          </div>
          {rowError && <div className="text-xs text-red-400">{rowError}</div>}
          <div className="flex gap-2">
            <button onClick={saveNew} disabled={saving} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold">Save</button>
            <button onClick={resetCreateForm} disabled={saving} className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-xs">Cancel</button>
          </div>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-800">
            <th className="px-4 py-2 font-medium">Category</th>
            <th className="px-4 py-2 font-medium">Event</th>
            <th className="px-4 py-2 font-medium">Title / Body</th>
            <th className="px-4 py-2 font-medium">FCM data (source_type / selected_tab)</th>
            <th className="px-4 py-2 font-medium">Audience</th>
            <th className="px-4 py-2 font-medium">Channels</th>
            {showRelevance && <th className="px-4 py-2 font-medium">Reaches this user?</th>}
            {editable && <th className="px-4 py-2 font-medium">Edit</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((entry) => {
            const withStatus = entry as NotificationCatalogEntryWithStatus;
            const isEditing = editingType === entry.notif_type;
            return (
              <tr key={entry.id} className="border-b border-gray-800/60 hover:bg-gray-800/30 align-top">
                <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">
                  {entry.category}
                  {entry.is_override && (
                    <div className="mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                      Edited
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5 text-white max-w-[220px]">{entry.event}</td>
                <td className="px-4 py-2.5 text-gray-400 max-w-[360px]">
                  {isEditing ? (
                    <div className="space-y-1.5">
                      <input
                        value={draftTitle}
                        onChange={(e) => setDraftTitle(e.target.value)}
                        className="w-full px-2 py-1 text-sm rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-indigo-500"
                        placeholder="Title"
                      />
                      <textarea
                        value={draftBody}
                        onChange={(e) => setDraftBody(e.target.value)}
                        rows={2}
                        className="w-full px-2 py-1 text-xs rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-indigo-500 resize-y"
                        placeholder="Body (supports {placeholders})"
                      />
                      <input
                        value={draftClickAction}
                        onChange={(e) => setDraftClickAction(e.target.value)}
                        list="click-action-options"
                        className="w-full px-2 py-1 text-xs rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-indigo-500"
                        placeholder="Click Action (Flutter route)"
                      />
                      {rowError && <div className="text-xs text-red-400">{rowError}</div>}
                    </div>
                  ) : (
                    <>
                      <div className="text-white font-medium">{entry.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{entry.body}</div>
                      {entry.click_action && (
                        <div className="text-[10px] text-indigo-400 mt-1 font-mono">
                          route: {entry.click_action}
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td className="px-4 py-2.5 max-w-[220px]">
                  {isEditing ? (
                    <div className="space-y-1.5">
                      <input
                        value={draftSourceType}
                        onChange={(e) => setDraftSourceType(e.target.value)}
                        list="source-type-options"
                        className="w-full px-2 py-1 text-xs rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-indigo-500 font-mono"
                        placeholder="source_type (blank = auto)"
                      />
                      <input
                        value={draftSelectedTab}
                        onChange={(e) => setDraftSelectedTab(e.target.value)}
                        list="selected-tab-options"
                        className="w-full px-2 py-1 text-xs rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-indigo-500 font-mono"
                        placeholder="selected_tab (blank = auto)"
                      />
                    </div>
                  ) : entry.source_type || entry.selected_tab ? (
                    <div className="text-[10px] font-mono space-y-0.5">
                      {entry.source_type && (
                        <div className="text-emerald-400">source_type: {entry.source_type}</div>
                      )}
                      {entry.selected_tab && (
                        <div className="text-sky-400">selected_tab: {entry.selected_tab}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-[10px] text-gray-600">auto-derived from notif_type</span>
                  )}
                </td>
                <td className="px-4 py-2.5 max-w-[200px]">
                  {isEditing ? (
                    <select
                      value={draftAppliesTo}
                      onChange={(e) => setDraftAppliesTo(e.target.value)}
                      className="w-full px-2 py-1 text-xs rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-indigo-500"
                    >
                      {Object.entries(AUDIENCE_META).map(([value, meta]) => (
                        <option key={value || "everyone"} value={value}>{meta.icon} {meta.label}</option>
                      ))}
                    </select>
                  ) : (
                    <AudienceBadge appliesTo={entry.applies_to} />
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {isEditing ? (
                    <div className="flex flex-col gap-1.5 text-xs text-gray-300">
                      {[["Socket", draftSocket, setDraftSocket], ["Bell", draftBell, setDraftBell], ["FCM", draftFcm, setDraftFcm]].map(([label, val, set]) => (
                        <label key={label as string} className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={val as boolean} onChange={(e) => (set as any)(e.target.checked)} />
                          {label as string}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <ChannelBadges channels={entry.channels} />
                  )}
                </td>
                {showRelevance && (
                  <td className="px-4 py-2.5 max-w-[260px]">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${withStatus.relevant ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-gray-800 text-gray-500 border-gray-700"}`}>
                      {withStatus.relevant ? "Yes" : "No"}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">{withStatus.relevance_note}</div>
                  </td>
                )}
                {editable && (
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {isEditing ? (
                      <div className="flex flex-col gap-1.5">
                        <button onClick={() => saveEdit(entry.notif_type)} disabled={saving} className="px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-medium">Save</button>
                        <button onClick={cancelEdit} disabled={saving} className="px-2.5 py-1 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 text-xs hover:border-gray-600">Cancel</button>
                        <button onClick={() => deleteTemplate(entry.notif_type)} disabled={saving} className="px-2.5 py-1 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs hover:bg-red-500/20">Delete</button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        <button onClick={() => startEdit(entry)} className="px-2.5 py-1 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 text-xs hover:border-gray-600">Edit</button>
                        <button onClick={() => deleteTemplate(entry.notif_type)} disabled={saving} className="px-2.5 py-1 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs hover:bg-red-500/20">Delete</button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Per-user lookup ──────────────────────────────────────────────────────────

const UserNotificationLookup: React.FC = () => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<{ id: string; name?: string; email?: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [status, setStatus] = useState<UserNotificationStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [activatingTokens, setActivatingTokens] = useState(false);
  const [activateMsg, setActivateMsg] = useState<string | null>(null);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      adminService.getUsers({ search: q, page: 1, page_size: 8 })
        .then((data) => { if (!cancelled) setResults((data.items as any[]) || []); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search]);

  const selectUser = async (userId: string) => {
    setLoadingStatus(true);
    setError(null);
    setResults([]);
    setActivateMsg(null);
    try {
      const data = await adminService.getUserNotificationStatus(userId);
      setStatus(data);
      setCurrentUserId(userId);
      setSearch(data.user.email || data.user.name || userId);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load notification status.");
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  };

  const updateSettings = async (next: { enabled?: boolean; channels?: { socket?: boolean; bell?: boolean; fcm?: boolean } }) => {
    if (!currentUserId || !status) return;
    setSavingSettings(true);
    setSettingsError(null);
    try {
      const updated = await adminService.updateUserNotificationSettings(currentUserId, next);
      setStatus({ ...status, notification_settings: updated });
    } catch (err: any) {
      setSettingsError(err?.response?.data?.detail || "Failed to update notification settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleActivateAllTokens = async () => {
    if (!currentUserId) return;
    setActivatingTokens(true);
    setActivateMsg(null);
    try {
      const res = await adminService.activateAllTokens(currentUserId);
      setActivateMsg(`✅ Activated ${res.activated_count} token(s). Refreshing…`);
      // Refresh status to show updated token list
      const data = await adminService.getUserNotificationStatus(currentUserId);
      setStatus(data);
    } catch (err: any) {
      setActivateMsg("❌ " + (err?.response?.data?.detail || "Failed to activate tokens."));
    } finally {
      setActivatingTokens(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setStatus(null); }}
          placeholder="Search user by name or email…"
          className="w-full px-3 py-2 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
        />
        {results.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
            {results.map((u) => (
              <button key={u.id} onClick={() => selectUser(u.id)} className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 transition-colors">
                <div className="font-medium">{u.name || "—"}</div>
                <div className="text-xs text-gray-500">{u.email}</div>
              </button>
            ))}
          </div>
        )}
        {searching && <div className="text-xs text-gray-500 mt-1">Searching…</div>}
      </div>

      {error && <div className="text-sm rounded-xl px-4 py-2.5 border bg-red-500/10 text-red-300 border-red-500/30">{error}</div>}
      {loadingStatus && <div className="text-sm text-gray-500">Loading user notification status…</div>}

      {status && !loadingStatus && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* User info */}
            <div className="bg-gray-800/50 rounded-xl p-3 border border-gray-800">
              <div className="text-xs text-gray-500">User</div>
              <div className="text-white font-medium">{status.user.name || status.user.email}</div>
              <div className="text-xs text-gray-500 mt-1">Role: <span className="text-indigo-400">{status.user.role}</span></div>
            </div>

            {/* Device tokens with activate-all */}
            <div className="bg-gray-800/50 rounded-xl p-3 border border-gray-800">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-gray-500">Active FCM tokens ({status.device_tokens.length})</div>
                <button
                  onClick={handleActivateAllTokens}
                  disabled={activatingTokens}
                  title="Reactivate all inactive tokens for this user"
                  className="px-2 py-0.5 rounded-lg text-[10px] font-medium bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/25 disabled:opacity-50 transition-colors"
                >
                  {activatingTokens ? "…" : "Activate All"}
                </button>
              </div>
              {activateMsg && <div className="text-[10px] text-gray-400 mb-1">{activateMsg}</div>}
              {status.device_tokens.length === 0 ? (
                <div className="text-xs text-gray-600">None registered</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {status.device_tokens.map((t, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-700 text-gray-300 border border-gray-600">
                      {t.platform || "device"}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* FCM topics */}
            <div className="bg-gray-800/50 rounded-xl p-3 border border-gray-800">
              <div className="text-xs text-gray-500 mb-1">Subscribed FCM topics ({status.fcm_topics.length})</div>
              {status.fcm_topics.length === 0 ? (
                <div className="text-xs text-gray-600">None — set preferences to derive topics</div>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-16 overflow-y-auto">
                  {status.fcm_topics.map((t) => (
                    <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-700 text-gray-300 border border-gray-600">{t}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notification overrides */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-800">
              <h3 className="text-sm font-semibold text-white">Notification overrides for this user</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                These override global event settings. If "Notifications enabled" is off, nothing is sent.
              </p>
            </div>
            <div className="px-4 py-3 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
                <input type="checkbox" checked={status.notification_settings.enabled} disabled={savingSettings} onChange={(e) => updateSettings({ enabled: e.target.checked })} />
                Notifications enabled
              </label>
              <div className="flex items-center gap-3 text-sm text-gray-300">
                {(["socket", "bell", "fcm"] as const).map((ch) => (
                  <label key={ch} className="flex items-center gap-1.5 cursor-pointer capitalize">
                    <input
                      type="checkbox"
                      checked={status.notification_settings.channels[ch]}
                      disabled={savingSettings || !status.notification_settings.enabled}
                      onChange={(e) => updateSettings({ channels: { ...status.notification_settings.channels, [ch]: e.target.checked } })}
                    />
                    {ch}
                  </label>
                ))}
              </div>
              {savingSettings && <span className="text-xs text-gray-500">Saving…</span>}
            </div>
            {settingsError && <div className="px-4 pb-3 text-xs text-red-400">{settingsError}</div>}
          </div>

          {/* Per-event delivery */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-800">
              <h3 className="text-sm font-semibold text-white">Per-event delivery for this user</h3>
            </div>
            <NotificationCatalogTable items={status.catalog} showRelevance />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Broadcast composer ───────────────────────────────────────────────────────

function toLocalDatetimeValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

interface AdminLogItem {
  _id: string;
  type: string;
  title: string;
  body: string;
  metadata?: Record<string, any>;
  source_module?: string;
  created_at: string;
}

// Deep-links an admin_notifications_log row to the relevant admin page —
// mirrors AdminNotificationsCenter.tsx's goToUser (kept separate: that's the
// compact 20-item bell dropdown, this is the full paginated audit-log page).
function alertsLogTarget(n: AdminLogItem): string {
  if (n.type === "deal_possible_duplicate" && n.metadata?.deal_id) {
    return `/admin/deals?deal_id=${encodeURIComponent(n.metadata.deal_id)}`;
  }
  if (
    (n.type === "gallery.pending_review" || n.type === "preview_pages.pending_review") &&
    n.metadata?.deal_id && n.metadata?.collection
  ) {
    return `/admin/deals/gallery-review?collection=${encodeURIComponent(n.metadata.collection)}&deal_id=${encodeURIComponent(n.metadata.deal_id)}`;
  }
  const searchTerm =
    n.metadata?.user_email || n.metadata?.user_name ||
    n.metadata?.seller_email || n.metadata?.seller_name ||
    n.metadata?.buyer_email || n.metadata?.buyer_name;
  return searchTerm ? `/admin/users?search=${encodeURIComponent(searchTerm)}` : "/admin/users";
}

// ── Admin Alerts Log — full paginated view of the durable, never-deleted
// admin_notifications_log collection (see record_admin_notification_log in
// backend/app/modules/notifications/service.py). The header bell
// (AdminNotificationsCenter) only shows the latest 20 with no way to page
// further back — this tab is the actual audit trail.
const AdminAlertsLogTab: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAdminNotificationsLog({ page, page_size: pageSize });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Admin Alerts Log</h2>
          <p className="text-sm text-gray-400 mt-1">
            Durable, never-deleted history of every notification sent to admins — verification requests,
            pending-market reviews, gallery moderation, and more. Unlike the bell icon (last 20, subject to a
            read-notification TTL), nothing is ever removed from here.
          </p>
        </div>
        <button onClick={fetchLog} className="text-xs text-gray-500 hover:text-gray-300 transition-colors whitespace-nowrap">↻ Refresh</button>
      </div>
      {loading ? (
        <div className="px-6 py-10 text-center text-gray-500 text-sm"><Spinner /></div>
      ) : items.length === 0 ? (
        <div className="px-6 py-10 text-center text-gray-500 text-sm">Nothing logged yet.</div>
      ) : (
        <div className="divide-y divide-gray-800/60">
          {items.map((n) => (
            <button
              key={n._id}
              type="button"
              onClick={() => navigate(alertsLogTarget(n))}
              className="w-full text-left px-6 py-4 hover:bg-gray-800/30 transition-colors"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm font-semibold text-white">{n.title}</span>
                <span className="text-xs text-gray-500 whitespace-nowrap">{formatIST(n.created_at)}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">{n.body}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-gray-800 text-gray-500 border border-gray-700">{n.type}</span>
                {n.source_module && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-gray-800 text-gray-500 border border-gray-700">{n.source_module}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      {total > 0 && (
        <div className="px-6 pb-4">
          <Pagination
            page={page - 1}
            pageSize={pageSize}
            total={total}
            onPageChange={(p) => setPage(p + 1)}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  );
};

const AdminNotificationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"catalog" | "broadcasts" | "users" | "alerts_log">("catalog");
  const { alertModal, showAlert } = useAlertModal();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [role, setRole] = useState<BroadcastRole>("all");
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [activities, setActivities] = useState<string[]>([]);
  const [deliveryMode, setDeliveryMode] = useState<BroadcastDeliveryMode>("all");
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<string>(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return toLocalDatetimeValue(d);
  });

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ countries: [], cities: [], tags: [], activities: [] });
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [preview, setPreview] = useState<BroadcastPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const previewDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [history, setHistory] = useState<PaginatedResponse<Broadcast> | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [catalog, setCatalog] = useState<NotificationCatalogEntry[]>([]);

  // Preview phone mock
  const previewTitle = title || "Notification title";
  const previewBody = body || "Your message will appear here…";

  // Prefill the composer when arriving from the community "Send FCM" action
  // (AdminPollsPage navigates here with ?source=community&title=…&role=…&countries=…).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("source") !== "community") return;
    const csv = (key: string) =>
      (searchParams.get(key) || "").split(",").map((s) => s.trim()).filter(Boolean);

    const t = searchParams.get("title");
    const b = searchParams.get("body");
    const dl = searchParams.get("deep_link");
    if (t) setTitle(t);
    if (b) setBody(b);
    if (dl) setDeepLink(dl);

    // role param may be a CSV ("buyer,seller"); broadcast accepts a single role.
    const roles = csv("role");
    setRole(roles.length === 1 && (roles[0] === "buyer" || roles[0] === "seller")
      ? (roles[0] as BroadcastRole)
      : "all");

    setCountries(csv("countries"));
    setCities(csv("cities"));
    setTags(csv("tags"));

    setFeedback({ kind: "success", text: "✏️ Prefilled from community post — review and send." });

    // Clear the params so a refresh doesn't re-prefill over manual edits.
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dealsService
      .getFilterOptions()
      .then((opts) => setFilterOptions({
        countries: opts.countries,
        cities: (opts as any).cities || [],
        tags: opts.tags,
        activities: opts.activities,
      }))
      .catch(() => {});
    adminService.getNotificationCatalog().then((data) => setCatalog(data.items)).catch(() => {});
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await adminService.getBroadcasts({ page, page_size: pageSize });
      setHistory(data);
    } catch {
      setHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { setPage(1); }, [pageSize]);

  // Auto-fetch reach estimate whenever role/filters change
  useEffect(() => {
    if (previewDebounce.current) clearTimeout(previewDebounce.current);
    previewDebounce.current = setTimeout(async () => {
      setLoadingPreview(true);
      try {
        const p = await adminService.previewBroadcast({
          title: "x", body: "x", role, countries, cities, tags, activities, delivery_mode: deliveryMode,
        });
        setPreview(p);
      } catch {
        setPreview(null);
      } finally {
        setLoadingPreview(false);
      }
    }, 600);
    return () => { if (previewDebounce.current) clearTimeout(previewDebounce.current); };
  }, [role, countries, cities, tags, activities, deliveryMode]);

  const toggle = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (value: string) => {
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      setFeedback({ kind: "error", text: "Title and body are required." });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      const payload: any = {
        title: title.trim(),
        body: body.trim(),
        deep_link: deepLink.trim() || null,
        role,
        countries,
        cities,
        tags,
        activities,
        delivery_mode: deliveryMode,
        scheduled_at: scheduleEnabled ? new Date(scheduledAt).toISOString() : null,
      };
      const result = await adminService.sendBroadcast(payload);
      if (result.status === "scheduled") {
        setFeedback({ kind: "success", text: `⏰ Broadcast scheduled for ${formatIST(result.scheduled_at!)}` });
      } else {
        const pushInfo = result.push_count ? ` · ${result.push_count} with FCM push` : "";
        setFeedback({ kind: "success", text: `✅ Sent to ${result.reach_count} ${payload.role === "all" ? "users" : payload.role + "s"} (in-app)${pushInfo}.` });
      }
      setTitle(""); setBody(""); setDeepLink(""); setRole("all");
      setCountries([]); setCities([]); setTags([]); setActivities([]);
      setDeliveryMode("all"); setScheduleEnabled(false);
      setPage(1);
      fetchHistory();
    } catch (err: any) {
      setFeedback({ kind: "error", text: err?.response?.data?.detail || "Failed to send broadcast." });
    } finally {
      setSending(false);
    }
  };

  const [editingBroadcast, setEditingBroadcast] = useState<Broadcast | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editScheduledAt, setEditScheduledAt] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [deletingBroadcastId, setDeletingBroadcastId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const openEdit = (b: Broadcast) => {
    setEditingBroadcast(b);
    setEditTitle(b.title);
    setEditBody(b.body);
    setEditScheduledAt(b.scheduled_at ? toLocalDatetimeValue(new Date(b.scheduled_at)) : "");
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editingBroadcast) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await adminService.patchBroadcast(editingBroadcast.id, {
        title: editTitle,
        body: editBody,
        scheduled_at: editScheduledAt ? new Date(editScheduledAt).toISOString() : undefined,
      });
      setHistory((h) => h ? { ...h, items: h.items.map((i) => i.id === updated.id ? updated : i) } : h);
      setEditingBroadcast(null);
    } catch (err: any) {
      setEditError(err?.response?.data?.detail || "Failed to save.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (b: Broadcast) => {
    if (!confirm(`Delete broadcast "${b.title}"?\n\nScheduled broadcasts will be cancelled.`)) return;
    setDeletingBroadcastId(b.id);
    try {
      await adminService.deleteBroadcast(b.id);
      fetchHistory(); // refresh — don't auto-remove, let server state drive the list
    } catch (err: any) {
      showAlert(err?.response?.data?.detail || "Failed to delete.");
    } finally {
      setDeletingBroadcastId(null);
    }
  };

  const handleResend = (b: Broadcast) => {
    // Pre-fill composer with this broadcast's content
    setTitle(b.title);
    setBody(b.body);
    setDeepLink(b.deep_link || "");
    setRole((b.filters.role as BroadcastRole) || "all");
    setCountries(b.filters.countries || []);
    setCities(b.filters.cities || []);
    setTags(b.filters.tags || []);
    setActivities(b.filters.activities || []);
    setScheduleEnabled(false);
    setFeedback(null);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filtersSummary = (b: Broadcast): string => {
    const parts: string[] = [];
    parts.push(b.filters.role === "all" ? "Everyone" : b.filters.role === "buyer" ? "Buyers" : "Sellers");
    if (b.filters.countries?.length) parts.push(`${b.filters.countries.length} country`);
    if (b.filters.cities?.length) parts.push(`${b.filters.cities.length} city`);
    if (b.filters.tags?.length) parts.push(`${b.filters.tags.length} tag`);
    if (b.filters.activities?.length) parts.push(`${b.filters.activities.length} activity`);
    return parts.join(" · ");
  };

  const activeFiltersCount = countries.length + cities.length + tags.length + activities.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <AdminPageHeader
        className="mb-0"
        title={<><span className="text-2xl">🔔</span> Notification Center</>}
        subtitle="Manage notification templates, send broadcasts, and check user status."
      />

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-x-6 border-b border-gray-800">
        <button onClick={() => setActiveTab("catalog")} className={`pb-3 text-sm font-medium transition-colors border-b-2 ${activeTab === "catalog" ? "text-white border-indigo-500" : "text-gray-500 border-transparent hover:text-gray-300"}`}>Event Catalog</button>
        <button onClick={() => setActiveTab("broadcasts")} className={`pb-3 text-sm font-medium transition-colors border-b-2 ${activeTab === "broadcasts" ? "text-white border-indigo-500" : "text-gray-500 border-transparent hover:text-gray-300"}`}>Broadcast Notifications</button>
        <button onClick={() => setActiveTab("users")} className={`pb-3 text-sm font-medium transition-colors border-b-2 ${activeTab === "users" ? "text-white border-indigo-500" : "text-gray-500 border-transparent hover:text-gray-300"}`}>User Status</button>
        <button onClick={() => setActiveTab("alerts_log")} className={`pb-3 text-sm font-medium transition-colors border-b-2 ${activeTab === "alerts_log" ? "text-white border-indigo-500" : "text-gray-500 border-transparent hover:text-gray-300"}`}>Admin Alerts Log</button>
      </div>

      {activeTab === "broadcasts" && (
        <div className="space-y-6">
          {/* Composer + Preview */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 xl:gap-5">
        {/* Composer */}
        <div className="xl:col-span-2 bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-5">
          <h2 className="text-base font-semibold text-white">Compose Message</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Title *</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                placeholder="e.g. 🔥 New deal just dropped"
                className="w-full px-3 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Deep link (optional)</label>
              <input
                value={deepLink}
                onChange={(e) => setDeepLink(e.target.value)}
                placeholder="/deals/123 or https://…"
                className="w-full px-3 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Body *</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Your notification message…"
              className="w-full px-3 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 transition-colors resize-y"
            />
          </div>

          {/* Audience */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Audience</label>
            <div className="flex gap-2 flex-wrap">
              {ROLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRole(opt.value)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all flex items-center gap-1.5 ${
                    role === opt.value
                      ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/40 shadow-sm shadow-indigo-500/10"
                      : "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600"
                  }`}
                >
                  <span>{opt.icon}</span> {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Delivery mode */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Delivery channels</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {([
                {
                  value: "all",
                  icon: "📲",
                  label: "FCM + Bell",
                  what: "Phone notification + in-app bell badge",
                  detail: "Works even when app is closed. User gets a phone push AND the 🔔 badge inside the app.",
                  color: "indigo",
                },
                {
                  value: "bell_only",
                  icon: "🔔",
                  label: "Bell only",
                  what: "In-app notification bell only",
                  detail: "No phone push. Notification appears inside the app when user opens it. Silent delivery.",
                  color: "sky",
                },
                {
                  value: "fcm_only",
                  icon: "📳",
                  label: "FCM push only",
                  what: "Phone push, no in-app badge",
                  detail: "Sends to devices with active FCM tokens. No bell badge stored — disappears if not acted on.",
                  color: "amber",
                },
                {
                  value: "toast_only",
                  icon: "💬",
                  label: "Toast only",
                  what: "Popup while app is open",
                  detail: "Fleeting in-app toast. Only visible if user has app open right now. No phone push, no bell badge.",
                  color: "emerald",
                },
              ] as { value: BroadcastDeliveryMode; icon: string; label: string; what: string; detail: string; color: string }[]).map((opt) => {
                const active = deliveryMode === opt.value;
                const colorMap: Record<string, string> = {
                  indigo: active ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-200" : "bg-gray-800/60 border-gray-700 hover:border-indigo-500/30",
                  sky:    active ? "bg-sky-500/15 border-sky-500/50 text-sky-200"         : "bg-gray-800/60 border-gray-700 hover:border-sky-500/30",
                  amber:  active ? "bg-amber-500/15 border-amber-500/50 text-amber-200"   : "bg-gray-800/60 border-gray-700 hover:border-amber-500/30",
                  emerald:active ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-200": "bg-gray-800/60 border-gray-700 hover:border-emerald-500/30",
                };
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDeliveryMode(opt.value)}
                    className={`flex flex-col gap-2 p-3 rounded-xl border text-left transition-all ${colorMap[opt.color]}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{opt.icon}</span>
                      <span className={`text-sm font-semibold ${active ? "" : "text-gray-300"}`}>{opt.label}</span>
                      {active && <span className="ml-auto w-2 h-2 rounded-full bg-current opacity-80 flex-shrink-0" />}
                    </div>
                    <p className={`text-xs font-medium ${active ? "" : "text-gray-400"}`}>{opt.what}</p>
                    <p className="text-[10px] text-gray-500 leading-relaxed">{opt.detail}</p>
                  </button>
                );
              })}
            </div>
            {deliveryMode === "fcm_only" && (
              <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 flex items-center gap-2">
                <span>⚠️</span>
                <span>Only {preview ? <strong>{preview.users_with_push} users</strong> : "users"} with active FCM device tokens will receive this — not all {preview?.total_users ?? "?"} {role === "all" ? "users" : role + "s"}.</span>
              </div>
            )}
            {deliveryMode === "toast_only" && (
              <div className="mt-2 px-3 py-2 rounded-lg bg-sky-500/10 border border-sky-500/20 text-xs text-sky-300 flex items-center gap-2">
                <span>ℹ️</span>
                <span>Toast only fires for users <strong>currently online</strong> in the app. Best for real-time alerts, not important announcements.</span>
              </div>
            )}
            {deliveryMode === "bell_only" && (
              <div className="mt-2 px-3 py-2 rounded-lg bg-sky-500/10 border border-sky-500/20 text-xs text-sky-300 flex items-center gap-2">
                <span>🔔</span>
                <span>Silent delivery — user sees it when they next open the app and check their notification bell. No phone buzz.</span>
              </div>
            )}
          </div>

          {/* Geo + Interest filters */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide flex items-center gap-2">
              Targeting filters
              {activeFiltersCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  {activeFiltersCount} active
                </span>
              )}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <ChipSection type="country" label="Countries" icon="🌍" options={filterOptions.countries} selected={countries} onToggle={toggle(setCountries)} />
              <ChipSection type="city" label="Cities" icon="🏙️" options={filterOptions.cities} selected={cities} onToggle={toggle(setCities)} />
              <ChipSection type="tag" label="Tags" icon="🏷️" options={filterOptions.tags} selected={tags} onToggle={toggle(setTags)} />
              <ChipSection type="activity" label="Activities" icon="🎯" options={filterOptions.activities} selected={activities} onToggle={toggle(setActivities)} />
            </div>
            {activeFiltersCount === 0 && (
              <p className="text-xs text-gray-600 mt-2">No filters = broadcast to the entire selected audience role.</p>
            )}
          </div>

          {/* Schedule */}
          <div className="p-4 rounded-xl border border-gray-800 bg-gray-800/30 space-y-3">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
                className="w-4 h-4 rounded accent-indigo-500"
              />
              <span className="text-sm font-medium text-white">Schedule for later</span>
              <span className="text-xs text-gray-500">— sends at the chosen time (IST)</span>
            </label>
            {scheduleEnabled && (
              <div className="flex items-center gap-3 ml-6">
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={toLocalDatetimeValue(new Date())}
                  className="px-3 py-2 rounded-xl bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                />
                <span className="text-xs text-amber-400 flex items-center gap-1">
                  ⏰ Will fire on schedule
                </span>
              </div>
            )}
          </div>

          {feedback && (
            <div className={`text-sm rounded-xl px-4 py-3 border flex items-center gap-2 ${
              feedback.kind === "success"
                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                : "bg-red-500/10 text-red-300 border-red-500/30"
            }`}>
              {feedback.text}
            </div>
          )}

          {/* Reach estimator */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 rounded-xl bg-gray-800/60 border border-gray-700/60">
            <div className="flex items-center gap-3">
              <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Estimated reach</div>
              {loadingPreview ? (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <div className="w-3 h-3 rounded-full border border-gray-600 border-t-gray-400 animate-spin" />
                  Calculating…
                </div>
              ) : preview ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-lg font-bold text-white">{preview.total_users.toLocaleString()}</span>
                    <span className="text-xs text-gray-400">
                      {role === "all" ? "users" : role + "s"}
                    </span>
                  </div>
                  <span className="text-gray-700">·</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-indigo-400">{preview.users_with_push.toLocaleString()}</span>
                    <span className="text-xs text-gray-400">with FCM push</span>
                  </div>
                  {preview.total_users === 0 && (
                    <span className="text-xs text-amber-400">⚠️ No matching users</span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-gray-600">—</span>
              )}
            </div>
            <div className="text-[10px] text-gray-600">In-app bell + FCM push</div>
          </div>

          <div className="flex justify-end pt-1">
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30 flex items-center gap-2"
            >
              {sending ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending…
                </>
              ) : (
                <>{scheduleEnabled ? "⏰ Schedule Broadcast" : "📣 Send Broadcast"}</>
              )}
            </button>
          </div>
        </div>

        {/* Live preview — phone mockup */}
        <div className="flex flex-col items-center gap-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide self-start">Live Preview</div>
          <div className="w-full max-w-[260px] bg-gray-950 rounded-[2.5rem] border-4 border-gray-700 p-4 shadow-2xl">
            {/* Phone top */}
            <div className="flex justify-center mb-3">
              <div className="w-16 h-1 bg-gray-700 rounded-full" />
            </div>
            {/* Notification card */}
            <div className="bg-gray-800 rounded-2xl p-3 shadow-lg border border-gray-700/60">
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-lg flex-shrink-0">
                  📣
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white truncate">{previewTitle}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">{previewBody}</div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700/60">
                <span className="text-[9px] text-gray-500">just now</span>
                <div className="flex gap-1">
                  {ROLE_OPTIONS.find((r) => r.value === role) && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-400 border border-indigo-500/20">
                      {ROLE_OPTIONS.find((r) => r.value === role)?.label}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Active filters summary inside phone */}
            {activeFiltersCount > 0 && (
              <div className="mt-2 px-1">
                <div className="text-[9px] text-gray-600">Targeting:</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {[...countries.slice(0, 2), ...cities.slice(0, 2), ...tags.slice(0, 2)].map((f) => (
                    <span key={f} className="text-[8px] px-1 py-0.5 rounded bg-gray-800 text-gray-500 border border-gray-700">{f}</span>
                  ))}
                  {activeFiltersCount > 6 && <span className="text-[8px] px-1 py-0.5 rounded bg-gray-800 text-gray-500">+{activeFiltersCount - 6}</span>}
                </div>
              </div>
            )}
          </div>

          {/* Schedule badge */}
          {scheduleEnabled && (
            <div className="w-full max-w-[260px] px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 flex items-center gap-2">
              <span>⏰</span>
              <span>Scheduled: {scheduledAt ? new Date(scheduledAt).toLocaleString("en-IN") : "—"}</span>
            </div>
          )}
        </div>
      </div>

      {/* History */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Broadcast History</h2>
          <button onClick={fetchHistory} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">↻ Refresh</button>
        </div>
        {loadingHistory ? (
          <div className="px-6 py-10 text-center text-gray-500 text-sm"><Spinner /></div>
        ) : !history || history.items.length === 0 ? (
          <div className="px-6 py-10 text-center text-gray-500 text-sm">No broadcasts yet.</div>
        ) : (
          <>
            {/* Broadcast cards — fully responsive */}
            <div className="divide-y divide-gray-800/60">
              {history.items.map((b) => (
                <div key={b.id} className="px-4 py-4 hover:bg-gray-800/20 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    {/* Left: title + body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-white">{b.title}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLES[b.status] || STATUS_STYLES.sent}`}>
                          {b.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">{b.body}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-500">
                        <span>👥 {filtersSummary(b)}</span>
                        {b.status !== "scheduled"
                          ? <span className="text-indigo-400 font-medium">
                              📊 {b.reach_count.toLocaleString()} reached
                              {b.push_count ? <span className="text-gray-500 font-normal"> · {b.push_count} FCM</span> : null}
                            </span>
                          : <span className="text-gray-600">reach TBD</span>
                        }
                        {b.status === "scheduled" && b.scheduled_at
                          ? <span className="text-amber-400">⏰ {formatIST(b.scheduled_at)}</span>
                          : b.sent_at ? <span>{formatIST(b.sent_at)}</span> : null
                        }
                      </div>
                    </div>

                    {/* Right: action buttons — always visible */}
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap">
                      {b.status === "scheduled" && (
                        <button
                          onClick={() => openEdit(b)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/25 transition-colors whitespace-nowrap"
                        >
                          ✏️ Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleResend(b)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/20 transition-colors whitespace-nowrap"
                      >
                        🔁 Resend
                      </button>
                      <button
                        onClick={() => handleDelete(b)}
                        disabled={deletingBroadcastId === b.id}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deletingBroadcastId === b.id ? "Deleting…" : "🗑️ Delete"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {history && history.total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={history.total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}
      </div>
      )}

      {/* Event catalog */}
      {activeTab === "catalog" && (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800">
          <h2 className="text-lg font-semibold text-white">Notification Event Catalog</h2>
          <p className="text-sm text-gray-400 mt-1">
            Every system event — who it targets (Audience), title/body templates, and channels (Socket toast, Bell, FCM push). Edits apply immediately.
          </p>
        </div>
        <NotificationCatalogTable items={catalog} editable onChanged={setCatalog} />
      </div>
      )}

      {/* Edit / Reschedule modal */}
      {editingBroadcast && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <h3 className="text-base font-semibold text-white">✏️ Edit Scheduled Broadcast</h3>
              <button onClick={() => setEditingBroadcast(null)} className="text-gray-500 hover:text-white transition-colors text-lg">×</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Title</label>
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Body</label>
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-indigo-500 resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Scheduled at (IST)</label>
                <input
                  type="datetime-local"
                  value={editScheduledAt}
                  onChange={(e) => setEditScheduledAt(e.target.value)}
                  min={toLocalDatetimeValue(new Date())}
                  className="px-3 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-indigo-500"
                />
                <p className="text-xs text-amber-400 mt-1">⏰ Changing the time creates a new timer — old one becomes a no-op.</p>
              </div>
              {editError && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{editError}</div>}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
              <button onClick={() => setEditingBroadcast(null)} className="px-4 py-2 rounded-xl bg-gray-800 border border-gray-700 text-gray-300 text-sm hover:border-gray-600 transition-colors">Cancel</button>
              <button
                onClick={saveEdit}
                disabled={editSaving}
                className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/20"
              >
                {editSaving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-user status */}
      {activeTab === "users" && (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Per-User Notification Status</h2>
          <p className="text-sm text-gray-400 mt-1">
            Look up a user to see their devices, FCM topics, notification overrides, and which events reach them.
          </p>
        </div>
        <UserNotificationLookup />
      </div>
      )}

      {/* Admin alerts audit log */}
      {activeTab === "alerts_log" && <AdminAlertsLogTab />}
      {alertModal}
    </div>
  );
};

export default AdminNotificationsPage;
